/**
 * Centralized shape coordinate access — every box a shape can be described by, in one place.
 *
 * A shape has more than one meaningful rectangle, and mixing them up is the source of a long tail
 * of off-by-half-the-shape bugs. The four that matter, and who wants each:
 *
 * | Box | What it is | Typical consumer |
 * | --- | --- | --- |
 * | **Geometric** | The persisted generator rect (`x`/`y`/`width`/`height`). Vertices are derived *from* it. | save/load, resize math |
 * | **Visual (local)** | Tight box around the drawn geometry, relative to the geometric rect's own origin. | render shell, rotated-AABB input |
 * | **Visual (canvas)** | The same tight box placed in canvas space — the drawable box. | selection chrome, snapping, alignment |
 * | **Rotated visual AABB** | Axis-aligned canvas box containing the *rotated* visual box. | left panel X/Y/W/H |
 *
 * Conventions that hold throughout:
 *
 * - **Rotation pivots on the geometric rect's center**, not the visual box's center. This matches
 *   `ShapeRenderer`'s `transformOrigin` and NSD's `AR="CTR"` anchor. A shape whose visual box is
 *   off-center (a triangle) therefore does *not* rotate about its own visual middle.
 * - **Nothing here measures the DOM.** Every value is computed from `ShapeData` alone, so it is
 *   available before first paint, never stale, and identical on the server.
 * - **Stroke is excluded** — these are path-geometry boxes. Inflate by half the stroke width at the
 *   call site if the inked extent is what you need.
 * - **No pixel snapping.** Snapping is a presentation concern that depends on canvas zoom and DPR;
 *   see `snapLayoutBoxToCanvasZoomGrid`. Keeping it out means these values stay exact and
 *   zoom-independent.
 *
 * Note this deliberately does *not* route the AABB through
 * `canvasAabbOfRotatedPathBounds`, which substitutes the geometric rect for the path bounds at ~0°
 * rotation. That substitution exists only because its `getBBox()`-derived input included stroke and
 * inflated unions; a computed, stroke-free visual box needs no such escape hatch, and honouring the
 * visual box at every angle is the entire point here.
 */

import type { ShapeData } from '@/lib/shapes/types/shape-data'
import {
  type Rect,
  type VisualInsetRatios,
  geometricRectOfShape,
  geometricToVisualRect,
  visualInsetRatiosForShape,
} from '@/lib/shapes/shape-visual-bounds'
import type { Point } from './types/point'

export type { Rect, VisualInsetRatios }

function degreesToRadians(deg: number): number {
  return ((Number.isFinite(deg) ? deg : 0) * Math.PI) / 180
}

/** The point every transform of this shape rotates about: the geometric rect's center. */
export function rotationPivotOfShape(shape: ShapeData): Point {
  return {
    x: shape.x + (shape.width ?? 0) / 2,
    y: shape.y + (shape.height ?? 0) / 2,
  }
}

/**
 * The visual box relative to the geometric rect's own origin.
 *
 * These are the `boundsX`/`boundsY`/`boundsWidth`/`boundsHeight` that the renderer's shell box and
 * the rotated-AABB math consume — the computed replacement for a `getBBox()` measurement.
 */
export function visualRectLocalOfShape(shape: ShapeData): Rect {
  const ratios = visualInsetRatiosForShape(shape)
  const width = shape.width ?? 0
  const height = shape.height ?? 0

  return {
    x: ratios.rx0 * width,
    y: ratios.ry0 * height,
    width: (ratios.rx1 - ratios.rx0) * width,
    height: (ratios.ry1 - ratios.ry0) * height,
  }
}

/**
 * The drawable box: the shape's tight visual box positioned in canvas space, unrotated.
 *
 * This is where the shape's geometry actually lands on the canvas. For a rotated shape this is
 * still the pre-rotation box — apply {@link rotatedAabbOfRect} about {@link rotationPivotOfShape}
 * for the axis-aligned extent after rotation.
 */
export function drawableRectOfShape(shape: ShapeData): Rect {
  return geometricToVisualRect(geometricRectOfShape(shape), visualInsetRatiosForShape(shape))
}

/**
 * The four corners of `rect` after rotating it `rotationDeg` about `pivot`, in NW → NE → SE → SW
 * order.
 *
 * Unlike {@link rotatedAabbOfRect} this keeps the box *oriented* — the actual quadrilateral the
 * shape occupies. Anything drawing chrome or placing resize anchors on a rotated shape wants these,
 * not the axis-aligned envelope.
 */
export function rotatedRectCorners(rect: Rect, pivot: Point, rotationDeg: number): Point[] {
  return [
    { x: rect.x, y: rect.y },
    { x: rect.x + rect.width, y: rect.y },
    { x: rect.x + rect.width, y: rect.y + rect.height },
    { x: rect.x, y: rect.y + rect.height },
  ].map((corner) => rotatePointAbout(corner, pivot, rotationDeg))
}

/** Rotate a single point `rotationDeg` about `pivot`. */
export function rotatePointAbout(point: Point, pivot: Point, rotationDeg: number): Point {
  const rad = degreesToRadians(rotationDeg)
  const cos = Math.cos(rad)
  const sin = Math.sin(rad)
  const dx = point.x - pivot.x
  const dy = point.y - pivot.y

  return {
    x: pivot.x + dx * cos - dy * sin,
    y: pivot.y + dx * sin + dy * cos,
  }
}

/** Axis-aligned bounds of `rect` after rotating it `rotationDeg` about `pivot`. */
export function rotatedAabbOfRect(rect: Rect, pivot: Point, rotationDeg: number): Rect {
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity

  for (const { x, y } of rotatedRectCorners(rect, pivot, rotationDeg)) {
    if (x < minX) minX = x
    if (x > maxX) maxX = x
    if (y < minY) minY = y
    if (y > maxY) maxY = y
  }

  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY }
}

/**
 * The axis-aligned canvas box containing the shape's rotated visual geometry — the X, Y, W and H
 * the left panel displays for a single shape.
 */
export function rotatedVisualAabbOfShape(shape: ShapeData): Rect {
  return rotatedAabbOfRect(
    drawableRectOfShape(shape),
    rotationPivotOfShape(shape),
    shape.rotation ?? 0
  )
}

/** The axis-aligned canvas box containing the rotated *geometric* rect, ignoring visual inset. */
export function rotatedGeometricAabbOfShape(shape: ShapeData): Rect {
  return rotatedAabbOfRect(
    geometricRectOfShape(shape),
    rotationPivotOfShape(shape),
    shape.rotation ?? 0
  )
}

/**
 * What selection chrome draws around a single object.
 *
 * **Chrome tracks the rotated *visual* box, not the geometric rect** — the frame hugs the geometry
 * the user can actually see, so an inset shape (triangle, star) no longer gets a frame standing off
 * its edges by the ellipse-inscribing gap.
 *
 * Returned unrotated (`rect`) plus the angle and pivot rather than as four corners, so a caller can
 * either render a rotated rect or take the corners from {@link rotatedRectCorners}. Two properties
 * of this frame are easy to get wrong:
 *
 * - **`pivot` is the geometric centre, not `rect`'s centre.** Rotation has to stay anchored to the
 *   geometric rect (NSD `AR="CTR"`) or persisted geometry changes meaning. For an inset shape those
 *   two points differ, so the frame rotates about a point that is *not* its own middle — and the
 *   centre-pivot marker will visibly sit off-centre. That is correct.
 * - **Handles on this frame do not map 1:1 onto geometric size.** A drag that moves the visual edge
 *   by `d` must change the geometric rect by `d / span` for that axis, since
 *   `visual = span × geometric`. Feed the desired visual rect through
 *   {@link visualToGeometricRect}; skipping that makes the shape lag the cursor by a factor of the
 *   inset ratio.
 */
export function selectionChromeFrameOfShape(shape: ShapeData): {
  rect: Rect
  rotationDeg: number
  pivot: Point
} {
  return {
    rect: drawableRectOfShape(shape),
    rotationDeg: shape.rotation ?? 0,
    pivot: rotationPivotOfShape(shape),
  }
}

/**
 * The axis-aligned chrome box for a multi-selection: the union of every member's *rotated visual*
 * box.
 *
 * A multi-selection has no single orientation, so unlike {@link selectionChromeFrameOfShape} this is
 * axis-aligned by nature — each member contributes its own rotated extent
 * ({@link rotatedVisualAabbOfShape}) and the union wraps them.
 *
 * Returns `null` for an empty selection.
 */
export function selectionChromeUnionOfShapes(shapes: ShapeData[]): Rect | null {
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  let found = false

  for (const shape of shapes) {
    const aabb = rotatedVisualAabbOfShape(shape)

    if (!Number.isFinite(aabb.x) || !Number.isFinite(aabb.y)) continue

    if (aabb.x < minX) minX = aabb.x
    if (aabb.y < minY) minY = aabb.y
    if (aabb.x + aabb.width > maxX) maxX = aabb.x + aabb.width
    if (aabb.y + aabb.height > maxY) maxY = aabb.y + aabb.height
    found = true
  }

  if (!found) return null

  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY }
}
